import { ReactNode, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useStateMachine } from "little-state-machine";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { ShipmentTypes } from "constants/index";
import useCustomBrokerPermission from "hooks/useCustomBrokerPermission";
import useFeatureFlag from "hooks/useFeatureFlag";
import PickupDeliveryProof from "../../../NavIcons/PickupDeliveryProof";
import MultistopProofActions from "../../../NavIcons/MultistopProofActions/MultistopProofActions";

interface CommonActionButtonsProps {
  isQuickRequest: boolean;
  tabTitle: string;
  intermediateInvoice?: ReactNode;
}

export default function CommonActionButtons({
  isQuickRequest,
  tabTitle,
  intermediateInvoice,
}: CommonActionButtonsProps) {
  const { state } = useStateMachine() as any;
  const { id } = useParams();
  const shipmentId = (id || state.shipmentId) as string;

  const intl = useIntl();
  const [isCustomBroker] = useCustomBrokerPermission(isQuickRequest);

  const trackingTabTitle = intl.formatMessage({ id: "general.tracking", defaultMessage: "Tracking" });

  const { data: shipmentBasicData }: any = useGetShipmentBasic(shipmentId, {
    enabled: !isQuickRequest && !!shipmentId,
    refetchOnMount: false,
  });

  const { shipment } = shipmentBasicData || {};
  const isOffPlatform = shipment?.is_off_platform_shipment || false;
  const shipmentStatus = shipment?.shipment_status;
  const isShipment = Boolean(shipmentStatus);
  const isMultiStop = shipment?.shipment_type === ShipmentTypes.MULTISTOP;
  const isMultistopPodEnabled = useFeatureFlag(FEATURE_FLAGS.WEB_10959_MULTISTOP_POD);

  const [isShipmentDelivered, setIsShipmentDelivered] = useState<boolean>(Boolean(shipmentStatus === "delivered"));
  useEffect(() => setIsShipmentDelivered(Boolean(shipmentStatus === "delivered")), [shipmentStatus]);

  const canShowTrackingProofActions =
    !isShipmentDelivered &&
    isShipment &&
    !isQuickRequest &&
    shipmentStatus !== "canceled" &&
    !isCustomBroker &&
    tabTitle === trackingTabTitle;

  if (!canShowTrackingProofActions) {
    return <>{intermediateInvoice}</>;
  }

  if (isMultiStop && isMultistopPodEnabled && !isOffPlatform) {
    return (
      <MultistopProofActions
        shipmentId={shipmentId}
        setIsShipmentDelivered={setIsShipmentDelivered}
        middleContent={intermediateInvoice}
      />
    );
  }

  return (
    <>
      <PickupDeliveryProof setIsShipmentDelivered={setIsShipmentDelivered} isOffPlatform={isOffPlatform} />
      {intermediateInvoice}
    </>
  );
}
